/**
 * Shared dictionary preference settings — stored in localStorage as JSON.
 *
 * Key: 'ep_dict_settings'
 */

export interface DictSettings {
  /** How the tooltip is triggered:
   *  'click'     — click on a word (default)
   *  'selection' — highlight a word with the mouse
   *  'off'       — disabled */
  trigger: 'click' | 'selection' | 'off';

  /** Show the Russian translation row */
  showTranslation: boolean;

  /** Show English definitions */
  showDefinitions: boolean;

  /** Show phonetic transcription */
  showPhonetics: boolean;

  /** Automatically add every looked-up word to the vocabulary */
  autoSave: boolean;
}

export const DICT_SETTINGS_KEY = 'ep_dict_settings';

export const DEFAULT_DICT_SETTINGS: DictSettings = {
  trigger: 'click',
  showTranslation: true,
  showDefinitions: true,
  showPhonetics: true,
  autoSave: false,
};

/** Read and merge with defaults (safe — never throws). */
export function readDictSettings(): DictSettings {
  try {
    const raw = localStorage.getItem(DICT_SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_DICT_SETTINGS };
    return { ...DEFAULT_DICT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_DICT_SETTINGS };
  }
}

/** Persist a partial patch on top of the existing settings. */
export function writeDictSettings(patch: Partial<DictSettings>): DictSettings {
  const next = { ...readDictSettings(), ...patch };
  try {
    localStorage.setItem(DICT_SETTINGS_KEY, JSON.stringify(next));
    // Broadcast so AppShell picks up the change instantly (same tab)
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: DICT_SETTINGS_KEY,
        newValue: JSON.stringify(next),
      })
    );
  } catch {}
  return next;
}
